import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { toast } from "sonner";
import { Calculator, Loader2, Flame, Activity, Weight, Settings2, ChevronDown } from "lucide-react";

function getToken() {
  return localStorage.getItem("nutramilo_access_token") || sessionStorage.getItem("nutramilo_access_token");
}

const ACTIVITY_LEVELS = [
  { v: "sedentary", bg: "Заседнал (1.2)", en: "Sedentary (1.2)" },
  { v: "light", bg: "Лек (1.375)", en: "Light (1.375)" },
  { v: "moderate", bg: "Умерен (1.55)", en: "Moderate (1.55)" },
  { v: "active", bg: "Активен (1.725)", en: "Active (1.725)" },
  { v: "very_active", bg: "Изключително (1.9)", en: "Very Active (1.9)" },
];
const GOALS = [
  { v: "maintain", bg: "Поддържане", en: "Maintain" },
  { v: "lose", bg: "Отслабване", en: "Lose" },
  { v: "gain", bg: "Качване", en: "Gain" },
  { v: "diabetes_type1", bg: "Диабет тип 1", en: "Diabetes T1" },
  { v: "diabetes_type2", bg: "Диабет тип 2", en: "Diabetes T2" },
  { v: "insulin_resistance", bg: "Инсулинова резистентност", en: "Insulin resistance" },
];
const ESTROGEN = [
  { v: "low", bg: "Нисък", en: "Low" },
  { v: "moderate", bg: "Умерен", en: "Moderate" },
  { v: "high", bg: "Висок", en: "High" },
];
const BIRADS = ["A", "B", "C", "D"];

/** Personalized DIL calculator — doctor mode.
 * Pulls patient defaults but exposes ALL formula overrides:
 * activity_level, goal, deficit_pct, cycle_day, anovulatory, estrogen_risk,
 * BIRADS, HOMA-IR override.
 */
export default function PatientDILPanel({ patientId, patient }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const sex = patient?.profile?.personal?.gender || "male";
  const isFemale = sex === "female";

  // Override state — empty string = use patient profile default
  const [showOverrides, setShowOverrides] = useState(false);
  const [activity, setActivity] = useState("");
  const [goal, setGoal] = useState("");
  const [deficit, setDeficit] = useState("");
  const [cycleDay, setCycleDay] = useState("");
  const [anovulatory, setAnovulatory] = useState(false);
  const [estrogenRisk, setEstrogenRisk] = useState("");
  const [birads, setBirads] = useState("");
  const [homaOverride, setHomaOverride] = useState("");

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    if (!patientId) return;
    setLoading(true);
    setError("");
    const body = {};
    if (activity) body.activity_level = activity;
    if (goal) body.goal = goal;
    if (deficit !== "") body.user_deficit = Number(deficit);
    if (cycleDay !== "") body.cycle_day = Number(cycleDay);
    if (anovulatory) body.is_anovulatory = true;
    if (estrogenRisk) body.estrogen_risk_level = estrogenRisk;
    if (birads) body.birads_risk = birads;
    if (homaOverride !== "") body.homa_ir_value = Number(homaOverride);
    try {
      const token = getToken();
      const { data } = await axios.post(`${API}/doctor/patients/${patientId}/calculate-dil`, body, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResult(data);
      toast.success(bg ? "DIL изчислен успешно" : "DIL calculated");
    } catch (err) {
      const msg = err.response?.data?.detail || (bg ? "Грешка при изчислението" : "Calculation error");
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const cardCls = `rounded-2xl border p-4 sm:p-5
    ${isDark ? "bg-[hsl(223,47%,10%)] border-white/5" : "bg-white border-gray-200"}`;

  const inputCls = `w-full px-2 py-1.5 text-xs rounded-md border outline-none
    ${isDark ? "bg-[hsl(222,47%,8%)] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`;

  return (
    <div className={cardCls} data-testid="patient-dil-panel">
      {/* iter397 — header layout is now mobile-first: title row + actions row stack on < sm, then sit side-by-side from sm onwards. Buttons take full width на phone. */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
        <div className="flex items-center gap-2 flex-shrink-0">
          <Calculator className="w-4 h-4 text-emerald-500" />
          <h3 className="text-sm font-bold">{bg ? "Персонализиран DIL" : "Personalized DIL"}</h3>
        </div>
        <div className="flex items-stretch gap-1.5 w-full sm:w-auto">
          <button
            onClick={() => setShowOverrides((v) => !v)}
            className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1 h-9 sm:h-8 px-2 rounded-lg text-[11px] font-semibold border
              ${isDark ? "border-white/10 text-violet-300 hover:bg-white/5" : "border-gray-200 text-violet-700 hover:bg-violet-50"}`}
            data-testid="dil-toggle-overrides"
          >
            <Settings2 className="w-3 h-3" />
            {bg ? "Параметри" : "Params"}
            <ChevronDown className={`w-3 h-3 transition-transform ${showOverrides ? "rotate-180" : ""}`} />
          </button>
          <button
            onClick={run}
            disabled={loading}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-3 h-9 sm:h-8 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600
              text-white text-xs font-semibold hover:opacity-95 disabled:opacity-60"
            data-testid="calc-dil-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5" />}
            {loading ? (bg ? "Изчислявам..." : "Calc...") : (bg ? "Изчисли" : "Calculate")}
          </button>
        </div>
      </div>

      {/* Inputs explainer */}
      <div className={`text-[10px] mb-2 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
        {bg
          ? "Формулата използва: тегло · ръст · възраст · пол · активност · цел · телесен състав · цикъл (♀) · HOMA-IR · естрогенен риск · BIRADS"
          : "Formula uses: weight · height · age · sex · activity · goal · body composition · cycle (♀) · HOMA-IR · estrogen risk · BIRADS"}
      </div>

      {/* Override drawer */}
      {showOverrides && (
        <div className={`grid grid-cols-2 sm:grid-cols-3 gap-2 p-3 rounded-xl mb-3 border
          ${isDark ? "bg-[hsl(222,47%,8%)] border-white/5" : "bg-gray-50 border-gray-200"}`}
          data-testid="dil-overrides-drawer">
          <label className="text-[11px]">
            <span className={isDark ? "text-gray-400" : "text-gray-600"}>{bg ? "Активност" : "Activity"}</span>
            <select className={inputCls} value={activity} onChange={(e) => setActivity(e.target.value)} data-testid="dil-activity">
              <option value="">{bg ? "(от профила)" : "(profile)"}</option>
              {ACTIVITY_LEVELS.map((o) => <option key={o.v} value={o.v}>{bg ? o.bg : o.en}</option>)}
            </select>
          </label>
          <label className="text-[11px]">
            <span className={isDark ? "text-gray-400" : "text-gray-600"}>{bg ? "Цел" : "Goal"}</span>
            <select className={inputCls} value={goal} onChange={(e) => setGoal(e.target.value)} data-testid="dil-goal">
              <option value="">{bg ? "(от профила)" : "(profile)"}</option>
              {GOALS.map((o) => <option key={o.v} value={o.v}>{bg ? o.bg : o.en}</option>)}
            </select>
          </label>
          {goal === "lose" && (
            <label className="text-[11px]">
              <span className={isDark ? "text-gray-400" : "text-gray-600"}>{bg ? "Дефицит %" : "Deficit %"}</span>
              <input type="number" min="5" max="30" step="1" className={inputCls} value={deficit} onChange={(e) => setDeficit(e.target.value)} placeholder="15" data-testid="dil-deficit" />
            </label>
          )}
          {isFemale && (
            <>
              <label className="text-[11px]">
                <span className={isDark ? "text-gray-400" : "text-gray-600"}>{bg ? "Ден от цикъл" : "Cycle day"}</span>
                <input type="number" min="1" max="35" className={inputCls} value={cycleDay} onChange={(e) => setCycleDay(e.target.value)} placeholder="14" data-testid="dil-cycle-day" />
              </label>
              <label className="text-[11px] flex items-center gap-1.5 mt-4">
                <input type="checkbox" checked={anovulatory} onChange={(e) => setAnovulatory(e.target.checked)} data-testid="dil-anovulatory" />
                <span className={isDark ? "text-gray-400" : "text-gray-600"}>{bg ? "Ановулаторен" : "Anovulatory"}</span>
              </label>
              <label className="text-[11px]">
                <span className={isDark ? "text-gray-400" : "text-gray-600"}>{bg ? "Естрогенен риск" : "Estrogen risk"}</span>
                <select className={inputCls} value={estrogenRisk} onChange={(e) => setEstrogenRisk(e.target.value)} data-testid="dil-estrogen">
                  <option value="">{bg ? "(авто)" : "(auto)"}</option>
                  {ESTROGEN.map((o) => <option key={o.v} value={o.v}>{bg ? o.bg : o.en}</option>)}
                </select>
              </label>
              <label className="text-[11px]">
                <span className={isDark ? "text-gray-400" : "text-gray-600"}>BI-RADS</span>
                <select className={inputCls} value={birads} onChange={(e) => setBirads(e.target.value)} data-testid="dil-birads">
                  <option value="">—</option>
                  {BIRADS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
            </>
          )}
          <label className="text-[11px] col-span-2">
            <span className={isDark ? "text-gray-400" : "text-gray-600"}>{bg ? "HOMA-IR override" : "HOMA-IR override"}</span>
            <input type="number" min="0" step="0.1" className={inputCls} value={homaOverride} onChange={(e) => setHomaOverride(e.target.value)} placeholder={bg ? "от лабораторни" : "from labs"} data-testid="dil-homa-override" />
          </label>
        </div>
      )}

      {error && (
        <div className={`rounded-lg p-2 text-xs ${isDark ? "bg-rose-500/10 text-rose-300" : "bg-rose-50 text-rose-700"}`} data-testid="dil-error">
          {error}
        </div>
      )}

      {result ? (
        <div className="space-y-3" data-testid="dil-result">
          {/* Main DIL score */}
          <div className={`rounded-xl p-3 ${isDark ? "bg-[hsl(222,47%,8%)]" : "bg-gradient-to-br from-emerald-50 to-teal-50"}`}>
            <div className="flex items-center gap-2 mb-1">
              <Flame className={`w-4 h-4 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
              <span className={`text-[10px] uppercase tracking-wider font-semibold ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                {bg ? "DIL скор" : "DIL score"}
              </span>
            </div>
            <div className="flex items-end gap-2">
              <div className="text-3xl font-bold" data-testid="dil-value">{result.personalized_dil ?? "—"}</div>
              {result.ref_dil != null && (
                <div className={`text-[11px] mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  / {result.ref_dil} {bg ? "референтен" : "reference"}
                </div>
              )}
            </div>
            {result.phenotype && (
              <div className={`text-[11px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg ? "Фенотип: " : "Phenotype: "}
                <span className="font-semibold">
                  {typeof result.phenotype === "object"
                    ? (bg ? result.phenotype.label_bg : result.phenotype.label) || result.phenotype.code
                    : result.phenotype}
                </span>
                {result.effective_goal && result.effective_goal !== (result.inputs_used?.goal || "") && (
                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${isDark ? "bg-amber-500/10 text-amber-300" : "bg-amber-50 text-amber-700"}`}>
                    {bg ? "цел префиксирана: " : "goal overridden: "}{result.effective_goal}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Energy */}
          <div className={`rounded-xl p-3 ${isDark ? "bg-[hsl(222,47%,8%)]" : "bg-gray-50"}`}>
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-sky-500" />
              <span className="text-xs font-bold">{bg ? "Енергия" : "Energy"}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Stat label_bg="BMR" label_en="BMR" value={result.energy?.bmr} suffix="kcal" isDark={isDark} bg={bg} />
              <Stat label_bg="TDEE" label_en="TDEE" value={result.energy?.tdee} suffix="kcal" isDark={isDark} bg={bg} />
              <Stat label_bg="Финал" label_en="Final" value={result.energy?.final_calories} suffix="kcal" isDark={isDark} bg={bg} />
            </div>
            {(result.energy?.deficit_pct || result.energy?.tef_factor) && (
              <div className="grid grid-cols-2 gap-2 mt-2 text-[10px]">
                {result.energy?.deficit_pct !== undefined && (
                  <div className={isDark ? "text-gray-500" : "text-gray-500"}>
                    {bg ? "Дефицит: " : "Deficit: "}<b>{result.energy.deficit_pct}%</b>
                  </div>
                )}
                {result.energy?.tef_factor !== undefined && (
                  <div className={isDark ? "text-gray-500" : "text-gray-500"}>
                    TEF: <b>{(result.energy.tef_factor * 100).toFixed(0)}%</b>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Macros */}
          <div className={`rounded-xl p-3 ${isDark ? "bg-[hsl(222,47%,8%)]" : "bg-gray-50"}`}>
            <div className="flex items-center gap-2 mb-2">
              <Weight className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-bold">{bg ? "Макроси" : "Macros"}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Stat label_bg="Протеин" label_en="Protein" value={result.macros?.protein_g} suffix="g" isDark={isDark} bg={bg} />
              <Stat label_bg="Въглехидрати" label_en="Carbs" value={result.macros?.carbs_g} suffix="g" isDark={isDark} bg={bg} />
              <Stat label_bg="Мазнини" label_en="Fats" value={result.macros?.fat_g} suffix="g" isDark={isDark} bg={bg} />
            </div>
          </div>

          {/* Body composition */}
          <div className={`rounded-xl p-3 ${isDark ? "bg-[hsl(222,47%,8%)]" : "bg-gray-50"}`}>
            <div className="flex items-center gap-2 mb-2">
              <Weight className="w-4 h-4 text-violet-500" />
              <span className="text-xs font-bold">{bg ? "Телесен състав" : "Body composition"}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Stat label_bg="BMI" label_en="BMI" value={result.body_composition?.bmi?.toFixed?.(1)} isDark={isDark} bg={bg} />
              <Stat label_bg="Телесни мазнини" label_en="Body fat" value={result.body_composition?.body_fat_pct?.toFixed?.(1)} suffix="%" isDark={isDark} bg={bg} />
              <Stat label_bg="Чиста маса" label_en="Lean mass" value={result.body_composition?.lean_mass_kg?.toFixed?.(1)} suffix="kg" isDark={isDark} bg={bg} />
              <Stat label_bg="Мазнина" label_en="Fat mass" value={result.body_composition?.fat_mass_kg?.toFixed?.(1)} suffix="kg" isDark={isDark} bg={bg} />
            </div>
          </div>

          {/* Cycle (female) */}
          {result.cycle && (
            <div className={`rounded-xl p-3 text-xs ${isDark ? "bg-[hsl(222,47%,8%)]" : "bg-pink-50"}`}>
              <div className="font-bold mb-1">{bg ? "Менструален цикъл" : "Menstrual cycle"}</div>
              <div className="grid grid-cols-3 gap-2">
                <div><span className="opacity-60">{bg ? "Ден" : "Day"}:</span> <b>{result.cycle.day ?? "—"}</b></div>
                <div><span className="opacity-60">{bg ? "Фаза" : "Phase"}:</span> <b>{bg ? (result.cycle.phase_bg || result.cycle.phase) : result.cycle.phase}</b></div>
                <div><span className="opacity-60">×</span> <b>{result.cycle.modifier?.toFixed(2)}</b></div>
              </div>
              {result.cycle.is_anovulatory && (
                <div className={`mt-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  ⚠ {bg ? "Ановулаторен — модификатор 1.08" : "Anovulatory — modifier 1.08"}
                </div>
              )}
            </div>
          )}

          {/* Lab/Estrogen */}
          {(result.homa_ir != null || result.estrogen_risk) && (
            <div className={`rounded-xl p-3 text-xs ${isDark ? "bg-[hsl(222,47%,8%)]" : "bg-gray-50"}`}>
              {result.homa_ir != null && (
                <div className="flex justify-between">
                  <span className={isDark ? "text-gray-400" : "text-gray-600"}>HOMA-IR</span>
                  <span className="font-semibold">{Number(result.homa_ir).toFixed(2)}</span>
                </div>
              )}
              {result.estrogen_risk && (
                <div className="flex justify-between mt-1">
                  <span className={isDark ? "text-gray-400" : "text-gray-600"}>{bg ? "Естрогенен риск" : "Estrogen risk"}</span>
                  <span className="font-semibold capitalize">
                    {result.estrogen_risk}{result.estrogen_multiplier && result.estrogen_multiplier !== 1 ? ` (×${result.estrogen_multiplier})` : ""}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      ) : !loading && !error ? (
        <p className={`text-xs text-center py-3 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {bg
            ? 'Натиснете "Изчисли" за персонализиран DIL. Параметрите идват от профила на пациента; натиснете "Параметри" за override.'
            : 'Click "Calculate" for personalized DIL. Parameters come from patient profile; click "Params" to override.'}
        </p>
      ) : null}
    </div>
  );
}

function Stat({ label_bg, label_en, value, suffix, isDark, bg }) {
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
        {bg ? label_bg : label_en}
      </div>
      <div className="text-sm font-bold">
        {value != null ? `${value}${suffix ? ` ${suffix}` : ""}` : "—"}
      </div>
    </div>
  );
}
